import { Import } from "lucide-react";
import * as React from "react";

import { useHasOrgRole } from "@/providers/auth/hooks";
import { SessionIngestedTriggers } from "@/shared/components/sessions";
import {
  type ExistingFilesPreview,
  type IngestionRuleRecord,
} from "@/shared/domain/ingestionRules";
import {
  useExistingFilesPreview,
  useIngestExistingFiles,
} from "@/shared/domain/ingestionRules/hooks";
import {
  Banner,
  ConfirmDialog,
  FormSection,
  RobotoButton,
} from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";

import styles from "./IngestionRulePages.module.css";

/** How many sample paths to list under the count. */
const SHOWN_SAMPLES = 3;

/**
 * Whether counting stopped, at its cap, before it found a single file: older
 * uploads were never checked, so there may be some.
 */
function stoppedBeforeFinding(preview: ExistingFilesPreview) {
  return preview.count === 0 && preview.count_is_lower_bound;
}

/** "12 files", "500 or more files", "1 file", "any matching files". */
function filesPhrase(preview: ExistingFilesPreview) {
  if (stoppedBeforeFinding(preview)) {
    return "any matching files";
  }
  const { count, count_is_lower_bound } = preview;
  const number = count_is_lower_bound ? `${count} or more` : `${count}`;
  return `${number} ${count === 1 && !count_is_lower_bound ? "file" : "files"}`;
}

/** "1 complete session", "40 complete sessions", "40 or more …". */
function sessionsPhrase({
  complete_session_count: count,
  count_is_lower_bound,
}: ExistingFilesPreview) {
  const number = count_is_lower_bound ? `${count} or more` : `${count}`;
  return `${number} complete ${count === 1 && !count_is_lower_bound ? "session" : "sessions"}`;
}

function isOne(preview: ExistingFilesPreview) {
  return preview.count === 1 && !preview.count_is_lower_bound;
}

const SECTION_TITLE = "Files uploaded before this rule";
const SECTION_HINT =
  "Auto-ingest only runs on new uploads. Ingest matching files that were already uploaded.";

/** What the card and its confirmation say about the files found. */
function backfillCopy(preview: ExistingFilesPreview) {
  if (stoppedBeforeFinding(preview)) {
    return {
      question: "Also ingest any files already uploaded that match?",
      summary:
        "None of the most recent uploads that were never ingested match this rule. Roboto stopped checking before it reached older ones, which may still match.",
      button: "Ingest matching files",
      confirmTitle: "Ingest any files already uploaded that match?",
    };
  }
  const phrase = filesPhrase(preview);
  const one = isOne(preview);
  return {
    question: `Also ingest the ${phrase} already uploaded that ${one ? "matches" : "match"}?`,
    summary: `${phrase} already uploaded ${one ? "matches" : "match"} this rule but ${one ? "was" : "were"} never ingested.`,
    button: one ? "Ingest this file" : `Ingest these ${phrase}`,
    confirmTitle: `Ingest ${phrase}?`,
  };
}

/**
 * On a rule that auto-ingests, a card with the files it matches that were
 * never ingested (uploaded before the rule, or while its auto-ingest was
 * paused), and, for org admins, a way to ingest them. Also offered when counting stopped before it
 * found any, since older uploads may still match.
 * `justSaved` asks the question outright, for the page shown after the rule
 * is saved.
 */
export function ExistingFilesBackfill({
  rule,
  justSaved = false,
}: {
  rule: IngestionRuleRecord;
  justSaved?: boolean;
}) {
  const isAdmin = useHasOrgRole("admin");
  const autoIngests = rule.auto_ingest && rule.trigger_id !== null;
  const preview = useExistingFilesPreview(rule.rule_id, {
    enabled: autoIngests,
  });
  const ingest = useIngestExistingFiles();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [queued, setQueued] = React.useState<ExistingFilesPreview | null>(null);

  if (queued !== null) {
    return (
      <FormSection title={SECTION_TITLE} hint={SECTION_HINT}>
        <p className={styles.text} data-testid="existing-files-queued">
          Roboto is starting ingestion runs for {filesPhrase(queued)} in the
          background. Their labels change as each one is ingested.
        </p>
      </FormSection>
    );
  }
  const found = preview.data;
  if (
    !autoIngests ||
    found === undefined ||
    (found.count === 0 && !found.count_is_lower_bound)
  ) {
    return null;
  }

  const copy = backfillCopy(found);
  const one = isOne(found);
  const shown = found.sample_paths.slice(0, SHOWN_SAMPLES);
  return (
    <FormSection
      title={SECTION_TITLE}
      hint={SECTION_HINT}
      actions={
        isAdmin ? (
          <RobotoButton
            variant="secondary"
            size="sm"
            icon={<Import aria-hidden="true" />}
            onClick={() => {
              ingest.reset();
              setConfirmOpen(true);
            }}
          >
            {copy.button}
          </RobotoButton>
        ) : undefined
      }
    >
      <div data-testid="existing-files-backfill">
        {justSaved && (
          <p className={styles.backfillQuestion}>{copy.question}</p>
        )}
        <p className={styles.text}>{copy.summary}</p>
        {shown.length > 0 && (
          <ul
            className={styles.samplePaths}
            data-testid="existing-file-samples"
          >
            {shown.map((path) => (
              <li key={path}>
                <code>{path}</code>
              </li>
            ))}
            {found.count > shown.length && <li>and more</li>}
          </ul>
        )}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={copy.confirmTitle}
        description="Roboto starts an ingestion run for each one in the background. The runs wait their turn in your organization's action queue, so new uploads may wait behind them."
        confirmLabel={one ? "Ingest file" : "Ingest files"}
        pending={ingest.isPending}
        onConfirm={() =>
          ingest.mutate(rule.rule_id, {
            onSuccess: (result) => {
              setConfirmOpen(false);
              setQueued(result);
            },
          })
        }
      >
        {found.complete_session_count > 0 && (
          <SessionIngestedTriggers
            orgId={rule.org_id}
            lead={`${sessionsPhrase(found)} ${found.complete_session_count === 1 ? "holds" : "hold"} ${one ? "this file" : "these files"}. After ${one ? "it is" : "they are"} ingested, each of those sessions is fully ingested again`}
            installsMetrics={null}
          />
        )}
        {ingest.error !== null && (
          <Banner variant="error">
            {userFacingErrorMessage(ingest.error)}
          </Banner>
        )}
      </ConfirmDialog>
    </FormSection>
  );
}
